// src/pages/public/RecoverEmailSent.jsx
//
// Pantalla de confirmación post-envío de email de recuperación.
// Aparece después de RecoverStep1 cuando el callable sendPasswordResetEmailFn
// devolvió success. El usuario llega acá vía navigate('/recuperar/enviado')
// con location.state.email para mostrarlo en el mensaje.
//
// Reemplaza el flujo viejo de RecoverStep2/RecoverStep3 (showroom OTP que
// nunca tuvo lógica funcional). El reseteo real ocurre en /auth/action
// cuando el usuario clickea el link del email Resend.

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { resetPassword, authErrorMessage } from '../../auth/authHelpers';
import { AuthLogo, ErrorBanner } from './_authShared';

export default function RecoverEmailSent() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state && location.state.email) || '';
  const [resentLoading, setResentLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    if (!email || resentLoading) return;
    setError('');
    setResentLoading(true);
    setResent(false);
    try {
      await resetPassword(email);
      setResent(true);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setResentLoading(false);
    }
  };

  return (
    <div className="mobile-root">
      <Helmet>
        <title>Revisa tu correo · Edge Elite</title>
      </Helmet>
      <section
        className="screen"
        style={{
          paddingTop: 60,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          paddingBottom: 40,
        }}
      >
        <AuthLogo />

        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 999,
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 12,
            marginBottom: 24,
          }}
        >
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </div>

        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            margin: 0,
            marginBottom: 8,
          }}
        >
          Revisa tu correo
        </h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, marginBottom: 22, maxWidth: 340, lineHeight: 1.55 }}>
          {email ? (
            <>
              Te enviamos un enlace a{' '}
              <strong style={{ color: 'var(--ink)' }}>{email}</strong> para crear una nueva contraseña. Toca el enlace para continuar.
            </>
          ) : (
            'Te enviamos un enlace para crear una nueva contraseña. Toca el enlace en tu correo para continuar.'
          )}
        </p>

        <ErrorBanner msg={error} />

        {resent && (
          <div
            style={{
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.3)',
              color: '#22C55E',
              padding: '10px 14px',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            Email reenviado correctamente
          </div>
        )}

        <button
          className="btn-primary accent"
          style={{ width: '100%', maxWidth: 320, opacity: resentLoading ? 0.7 : 1 }}
          onClick={handleResend}
          disabled={resentLoading || !email}
        >
          {resentLoading ? 'Reenviando…' : 'Reenviar email'}
        </button>

        <div style={{ marginTop: 18 }}>
          <a
            onClick={() => navigate('/login')}
            style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Volver a iniciar sesión
          </a>
        </div>

        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: 'var(--muted)' }}>
          ¿Email incorrecto?{' '}
          <a
            onClick={() => navigate('/recuperar')}
            style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
          >
            Cambiar
          </a>
        </div>

        <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 30, maxWidth: 320, lineHeight: 1.5 }}>
          Si no llega en unos minutos, revisa la carpeta de spam o promociones.
        </p>
      </section>
    </div>
  );
}
